import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { AgeGroup, isLessonBookable, Lesson, Term, TierFeatureKey } from '@court-sync/shared';
import { Button } from '@/components/ui/Button';
import { useAgeGroups, useLessons, usePrograms, useTerms } from '@/hooks/useDataStore';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import { lessonEnrollmentService } from '@/services/lessonEnrollment';
import { getCampInstanceTrackLabel } from '@/pages/dashboards/academy-owner/camps/campDisplayUtils';
import toast from 'react-hot-toast';

function formatLessonOptionLabel(
  lesson: Lesson,
  ageGroups: AgeGroup[],
  programs: Array<{ id: string; name: string }>
): string {
  const trackLabel = getCampInstanceTrackLabel(lesson, { ageGroups, programs });
  if (trackLabel) {
    return `${lesson.name} · ${trackLabel} (${lesson.lessonType})`;
  }
  return `${lesson.name} (${lesson.lessonType})`;
}

/** Active group, private, and camp lessons that staff can still enroll into (not ended). */
export function filterStaffEnrollableLessons(
  lessons: Lesson[],
  terms: Term[],
  options?: { excludeLessonId?: string }
): Lesson[] {
  const termById = new Map(terms.map((t) => [t.id, t]));
  const excludeLessonId = options?.excludeLessonId;

  return lessons
    .filter((lesson) => {
      if (excludeLessonId && lesson.id === excludeLessonId) return false;
      if (!lesson.isActive) return false;

      if (lesson.lessonType === 'group' && lesson.termId) {
        const term = termById.get(lesson.termId);
        return isLessonBookable(lesson, term ?? null);
      }

      if (lesson.lessonType === 'private') {
        if (lesson.termId) {
          const term = termById.get(lesson.termId);
          return isLessonBookable(lesson, term ?? null);
        }
        return isLessonBookable(lesson, null);
      }

      if (lesson.lessonType === 'camp') {
        return isLessonBookable(lesson, null);
      }

      return false;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface AddStudentsToLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  studentIds: string[];
  onDone: () => void;
  /** When set, choosing a lesson calls this and does not enroll (e.g. move — parent confirms, then enrolls + removes). */
  onLessonPicked?: (p: { lesson: Lesson; termId?: string | null }) => void;
  /** Overrides dialog title. */
  title?: string;
  /** Overrides primary button when `onLessonPicked` is set. */
  pickPrimaryLabel?: string;
  /** If set, this lesson is excluded from the list (e.g. current booking when moving). */
  excludeLessonId?: string;
}

/**
 * Add selected students to a lesson (owner bulk action).
 */
export function AddStudentsToLessonModal(props: AddStudentsToLessonModalProps) {
  if (!props.isOpen) return null;
  return <AddStudentsToLessonModalContent {...props} />;
}

function AddStudentsToLessonModalContent({
  onClose,
  academyId,
  studentIds,
  onDone,
  onLessonPicked,
  title,
  pickPrimaryLabel,
  excludeLessonId,
}: AddStudentsToLessonModalProps) {
  const { lessons, isLoading: lessonsLoading } = useLessons(academyId);
  const { terms, isLoading: termsLoading } = useTerms(academyId);
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { programs: cachedPrograms, isLoading: programsLoading } = usePrograms(academyId, {
    includeInactive: true,
    enabled: !!academyId && hasProgramsAccess,
  });
  const programs = hasProgramsAccess ? cachedPrograms : [];
  const { ageGroups, isLoading: ageGroupsLoading } = useAgeGroups(academyId);

  const [lessonId, setLessonId] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  const enrollableLessons = useMemo(
    () => filterStaffEnrollableLessons(lessons, terms, { excludeLessonId }),
    [lessons, terms, excludeLessonId]
  );

  const loading =
    (lessonsLoading || termsLoading || programsLoading || ageGroupsLoading) &&
    enrollableLessons.length === 0 &&
    lessons.length === 0;

  const selectedLesson = enrollableLessons.find((l) => l.id === lessonId);
  const canEnroll = Boolean(
    lessonId &&
      selectedLesson &&
      (selectedLesson.lessonType === 'camp' ||
        selectedLesson.lessonType === 'private' ||
        Boolean(selectedLesson.termId))
  );

  const pickMode = Boolean(onLessonPicked);

  const handlePrimary = async () => {
    if (!selectedLesson) return;
    if (selectedLesson.lessonType === 'group' && !selectedLesson.termId) return;

    if (pickMode && onLessonPicked) {
      onLessonPicked({
        lesson: selectedLesson,
        termId:
          selectedLesson.lessonType === 'camp' ? undefined : (selectedLesson.termId ?? undefined),
      });
      setLessonId('');
      onClose();
      return;
    }
    setEnrolling(true);
    let ok = 0;
    let fail = 0;
    for (const sid of studentIds) {
      const result = await lessonEnrollmentService.enrollStudent(selectedLesson.id, {
        studentId: sid,
        academyId,
        ...(selectedLesson.termId && selectedLesson.lessonType !== 'camp'
          ? { termId: selectedLesson.termId }
          : {}),
      });
      if (result.success) ok++;
      else fail++;
    }
    setEnrolling(false);
    if (ok) toast.success(`Enrolled ${ok} student(s)`);
    if (fail) toast.error(`${fail} enrollment(s) failed (may already be booked)`);
    onDone();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-950">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title ?? (pickMode ? 'Move to another lesson' : 'Add to lesson')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {pickMode
            ? 'Choose the lesson to move to. You will confirm before any change is made.'
            : `${studentIds.length} student${studentIds.length !== 1 ? 's' : ''} — choose a group, private, or camp lesson.`}
        </p>
        {loading ? (
          <p className="py-4 text-sm text-gray-500">Loading lessons…</p>
        ) : (
          <select
            value={lessonId}
            onChange={(e) => setLessonId(e.target.value)}
            className="mb-4 w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            aria-label="Select lesson"
          >
            <option value="">Select a lesson</option>
            {enrollableLessons.map((l) => (
              <option key={l.id} value={l.id}>
                {formatLessonOptionLabel(l, ageGroups, programs)}
              </option>
            ))}
          </select>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={enrolling}>
            Cancel
          </Button>
          <Button type="button" onClick={handlePrimary} disabled={enrolling || !lessonId || !canEnroll}>
            {enrolling
              ? 'Enrolling…'
              : pickMode
                ? (pickPrimaryLabel ?? 'Continue')
                : 'Enroll all'}
          </Button>
        </div>
      </div>
    </div>
  );
}
